import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import type { IFormContext } from 'Containers/FormContextHandler';
import { withFormContext } from 'Containers/FormContextHandler';
import colors from '../../../../../../config/theme/colors';

const SectionName = ({ title }: { title: string }) => {
  return (
    <div style={{ color: colors.darkGray, marginBottom: '1rem' }}>
      <FormattedMessage id={title} />
    </div>
  );
};

const GeneticTextResultInput = ({
  getSubcategoryDetailValue,
  subCatDetailOnChange,
}: {
  getSubcategoryDetailValue: (name: keyof IPolyneuroEtiologySubCategoryDetails) => void;
  subCatDetailOnChange: (name: keyof IPolyneuroEtiologySubCategoryDetails, values: TOnChangeValues) => void;
}) => {
  return (
    <div style={{ marginBottom: '2rem' }}>
      <SectionName title="diagnosis.polyneuro.etiology.subCategoryDetails.geneticTestResult" />
      <InputHandler
        type="Radio"
        editing={true}
        name="geneticTestResult"
        formData={{
          document: { geneticTestResult: getSubcategoryDetailValue('geneticTestResult') },
          onChange: values => subCatDetailOnChange('geneticTestResult', values),
        }}
        options={['pathogenic', 'likelyPathogenic', 'variantOfUncertainSignificance', 'negative', 'notPerformed']}
        optionFormatter={o => <FormattedMessage id={'diagnosis.polyneuro.etiology.opts.' + o} />}
      />
    </div>
  );
};

const SpecifyInput = ({
  getSubcategoryDetailValue,
  subCatDetailOnChange,
}: {
  getSubcategoryDetailValue: (name: keyof IPolyneuroEtiologySubCategoryDetails) => void;
  subCatDetailOnChange: (name: keyof IPolyneuroEtiologySubCategoryDetails, values: TOnChangeValues) => void;
}) => {
  return (
    <div style={{ marginBottom: '2rem' }}>
      <SectionName title="diagnosis.polyneuro.etiology.subCategoryDetails.specify" />
      <InputHandler
        type="TextField"
        editing={true}
        name="specify"
        formData={{
          document: { specify: getSubcategoryDetailValue('specify') },
          onChange: values => subCatDetailOnChange('specify', values),
        }}
      />
    </div>
  );
};

const ParaproteinDetectedInput = ({
  getSubcategoryDetailValue,
  subCatDetailOnChange,
}: {
  getSubcategoryDetailValue: (name: keyof IPolyneuroEtiologySubCategoryDetails) => void;
  subCatDetailOnChange: (name: keyof IPolyneuroEtiologySubCategoryDetails, values: TOnChangeValues) => void;
}) => {
  return (
    <>
      <div style={{ marginBottom: '2rem' }}>
        <SectionName title="diagnosis.polyneuro.etiology.subCategoryDetails.paraproteinDetected" />
        <InputHandler
          type="Radio"
          editing={true}
          name="paraproteinDetected"
          formData={{
            document: { paraproteinDetected: getSubcategoryDetailValue('paraproteinDetected') },
            onChange: values => subCatDetailOnChange('paraproteinDetected', values),
          }}
          options={['igm', 'igg', 'iga']}
          optionFormatter={o => <FormattedMessage id={'diagnosis.polyneuro.etiology.opts.' + o} />}
        />
      </div>
      <div style={{ marginBottom: '2rem' }}>
        <SectionName title="diagnosis.polyneuro.etiology.subCategoryDetails.typeOfParaproteinemia" />
        <InputHandler
          type="Radio"
          editing={true}
          name="typeOfParaproteinemia"
          formData={{
            document: { typeOfParaproteinemia: getSubcategoryDetailValue('typeOfParaproteinemia') },
            onChange: values => subCatDetailOnChange('typeOfParaproteinemia', values),
          }}
          options={[
            'mgus',
            'antiMAGNeuropathy',
            'poems',
            'alAmyloidosis',
            'waldenströmMacroglobulinemia',
            'multipleMyeloma',
            'canomad',
            'cryoglobulinemiaAssociatedNeuropathy',
            'otherParaproteinemiaAssociatedNeuropathy',
          ]}
          optionFormatter={o => <FormattedMessage id={'diagnosis.polyneuro.etiology.opts.' + o} />}
          optionSpecificElements={[
            {
              antiMAGNeuropathy: (
                <div>
                  <SectionName title="diagnosis.polyneuro.etiology.subCategoryDetails.antiMAGAntibodyDetected" />
                  <InputHandler
                    type="Radio"
                    editing
                    name="antiMAGAntibodyDetected"
                    formData={{
                      document: { antiMAGAntibodyDetected: getSubcategoryDetailValue('antiMAGAntibodyDetected') },
                      onChange: values => subCatDetailOnChange('antiMAGAntibodyDetected', values),
                    }}
                    options={['yes', 'no']}
                    optionFormatter={o => <FormattedMessage id={'general.' + o} />}
                  />
                </div>
              ),
              otherParaproteinemiaAssociatedNeuropathy: (
                <div>
                  <SectionName title="diagnosis.polyneuro.etiology.subCategoryDetails.specify" />
                  <InputHandler
                    editing
                    type="TextField"
                    name="specify"
                    formData={{
                      document: { specify: getSubcategoryDetailValue('specify') },
                      onChange: values => subCatDetailOnChange('specify', values),
                    }}
                  />
                </div>
              ),
            },
          ]}
        />
      </div>
    </>
  );
};

const DiagnosticCertaintyInput = ({
  getSubcategoryDetailValue,
  subCatDetailOnChange,
}: {
  getSubcategoryDetailValue: (name: keyof IPolyneuroEtiologySubCategoryDetails) => void;
  subCatDetailOnChange: (name: keyof IPolyneuroEtiologySubCategoryDetails, values: TOnChangeValues) => void;
}) => {
  return (
    <div style={{ marginBottom: '2rem' }}>
      <SectionName title="diagnosis.polyneuro.etiology.subCategoryDetails.diagnosticCertainty" />
      <InputHandler
        type="Radio"
        editing={true}
        name="diagnosticCertainty"
        formData={{
          document: { diagnosticCertainty: getSubcategoryDetailValue('diagnosticCertainty') },
          onChange: values => subCatDetailOnChange('diagnosticCertainty', values),
        }}
        options={['definite', 'probable', 'possible']}
        optionFormatter={o => <FormattedMessage id={'diagnosis.polyneuro.etiology.opts.' + o} />}
      />
    </div>
  );
};

const mainCategory = ['inflammatory', 'hereditary', 'otherNeurological', 'unknown'];

const inflammatorySub = ['cidp', 'gbs', 'mmn', 'paraproteinemiaAssociatedNeuropathy', 'otherInflammatoryNeuropathy'];

const hereditarySub = ['charcotMarieToothDisease', 'otherHereditaryNeuropathy'];

const PolyneuroEtiologyForm = ({ editing, formData }: IFormContext<IPolyneuroEtiology>): React.JSX.Element => {
  const selectedMain = formData.document.mainCategory;

  const subCatDetailOnChange = (name: keyof IPolyneuroEtiologySubCategoryDetails, values: TOnChangeValues) => {
    const value = values[name];

    const existingDetails = formData.document.subCategoryDetails;

    if (existingDetails?.[name]) {
      let newDetails = { ...existingDetails, [name]: value };
      // Erase possible subsubfields
      newDetails =
        name === 'typeOfParaproteinemia'
          ? { ...newDetails, antiMAGAntibodyDetected: undefined, specify: undefined }
          : newDetails;
      formData.onChange?.({ subCategoryDetails: newDetails });
    } else {
      formData.onChange?.({ subCategoryDetails: { ...existingDetails, [name]: value } });
    }
  };

  const getSubcategoryDetailValue = (name: keyof IPolyneuroEtiologySubCategoryDetails) => {
    return formData.document.subCategoryDetails?.[name];
  };

  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!!editing}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormRow title="diagnosis.polyneuro.etiology.mainCategory.title">
        <InputHandler
          type="Radio"
          editing={!!editing}
          name="mainCategory"
          formData={formData}
          options={mainCategory}
          optionFormatter={(name: string | number): React.JSX.Element => (
            <FormattedMessage id={`diagnosis.polyneuro.etiology.mainCategory.${name}`} />
          )}
          dependentFieldsList={(): string[] => ['subCategory']}
          dependentFieldsRemovalWarning
        />
      </FormRow>
      <FormRow
        title="diagnosis.polyneuro.etiology.subCategory.title"
        condition={selectedMain ? ['inflammatory', 'hereditary'].includes(selectedMain) : undefined}
      >
        <InputHandler
          type="Radio"
          editing={!!editing}
          name="subCategory"
          formData={formData}
          options={
            selectedMain === 'inflammatory' ? inflammatorySub : selectedMain === 'hereditary' ? hereditarySub : []
          }
          optionFormatter={(name: string | number): React.JSX.Element => (
            <FormattedMessage id={`diagnosis.polyneuro.etiology.subCategory.${name}`} />
          )}
          dependentFieldsList={(): string[] => ['subCategoryDetails']}
          dependentFieldsRemovalWarning
          optionSpecificElements={[
            {
              cidp: (
                <DiagnosticCertaintyInput
                  subCatDetailOnChange={subCatDetailOnChange}
                  getSubcategoryDetailValue={getSubcategoryDetailValue}
                />
              ),
              gbs: (
                <DiagnosticCertaintyInput
                  subCatDetailOnChange={subCatDetailOnChange}
                  getSubcategoryDetailValue={getSubcategoryDetailValue}
                />
              ),
              mmn: (
                <DiagnosticCertaintyInput
                  subCatDetailOnChange={subCatDetailOnChange}
                  getSubcategoryDetailValue={getSubcategoryDetailValue}
                />
              ),
              paraproteinemiaAssociatedNeuropathy: (
                <ParaproteinDetectedInput
                  subCatDetailOnChange={subCatDetailOnChange}
                  getSubcategoryDetailValue={getSubcategoryDetailValue}
                />
              ),
              otherInflammatoryNeuropathy: (
                <SpecifyInput
                  subCatDetailOnChange={subCatDetailOnChange}
                  getSubcategoryDetailValue={getSubcategoryDetailValue}
                />
              ),
              charcotMarieToothDisease: (
                <GeneticTextResultInput
                  subCatDetailOnChange={subCatDetailOnChange}
                  getSubcategoryDetailValue={getSubcategoryDetailValue}
                />
              ),
              otherHereditaryNeuropathy: (
                <SpecifyInput
                  subCatDetailOnChange={subCatDetailOnChange}
                  getSubcategoryDetailValue={getSubcategoryDetailValue}
                />
              ),
            },
          ]}
        />
      </FormRow>
    </React.Fragment>
  );
};

export default withFormContext(PolyneuroEtiologyForm);
